import { Link } from "react-router-dom";
import { Button } from "@jambushrushti/components/ui/button";
import {
  BookOpen,
  Search,
  Library,
  ScrollText,
  Users,
  Heart,
} from "lucide-react";
import { gurudevBio } from "@jambushrushti/data/gurudevData";

// Lotus SVG component
const LotusIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C12 2 9 6 9 10C9 12 10 14 12 14C14 14 15 12 15 10C15 6 12 2 12 2Z" />
    <path
      d="M6 8C6 8 2 10 2 14C2 16 4 18 6 18C8 18 10 16 10 14C10 10 6 8 6 8Z"
      opacity="0.7"
    />
    <path
      d="M18 8C18 8 22 10 22 14C22 16 20 18 18 18C16 18 14 16 14 14C14 10 18 8 18 8Z"
      opacity="0.7"
    />
    <path
      d="M12 14C12 14 8 16 8 20C8 21 9 22 12 22C15 22 16 21 16 20C16 16 12 14 12 14Z"
      opacity="0.5"
    />
  </svg>
);

export function HeroSection() {
  return (
    <section className="bg-gradient-hero lotus-pattern relative flex min-h-[90vh] items-center overflow-hidden">
      <div className="container relative z-10 mx-auto px-6 py-12 lg:px-12 lg:py-16">
        <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-10">
          {/* Content */}
          <div className="order-2 space-y-5 lg:order-1">
            {/* Badge */}
            <div
              className="inline-flex items-center gap-2 rounded-full border px-4 py-2"
              style={{
                backgroundColor: "rgba(255,255,255,0.6)",
                borderColor: "#E5DFD4",
              }}
            >
              <LotusIcon className="h-4 w-4 text-[#C9A227]" />
              <span
                className="text-sm font-medium"
                style={{ fontFamily: "Inter, sans-serif", color: "#7A6F5D" }}
              >
                Preserving Ancient Wisdom
              </span>
            </div>

            {/* Sanskrit subtitle */}
            <p
              className="text-base font-medium"
              style={{ fontFamily: "Inter, sans-serif", color: "#C6933A" }}
            >
              आगमप्रज्ञ पू. मुनिराज श्री जम्बू विजयजी महाराज साहेब
            </p>

            {/* Main Title */}
            <div className="space-y-0">
              <h1
                className="text-4xl font-bold leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl"
                style={{
                  fontFamily: "Playfair Display, serif",
                  color: "#2D2A26",
                }}
              >
                Jain Knowledge &
              </h1>
              <h1
                className="text-4xl font-bold leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl"
                style={{
                  fontFamily: "Playfair Display, serif",
                  background:
                    "linear-gradient(135deg, #C6933A 0%, #E3C075 50%, #C6933A 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Research Ecosystem
              </h1>
            </div>

            {/* Description */}
            <p
              className="max-w-lg text-base leading-relaxed lg:text-lg"
              style={{ fontFamily: "Inter, sans-serif", color: "#6B6764" }}
            >
              Explore the profound teachings of Gurudev Muni Jambuvijayaji
              Maharaj Saheb (1923–2009). Discover sacred texts, research tools,
              and a community dedicated to preserving Jain philosophy.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-3 pt-1">
              <Button
                size="lg"
                className="rounded-lg px-6 py-5 text-sm font-medium text-white shadow-md transition-all hover:shadow-lg"
                style={{
                  fontFamily: "Inter, sans-serif",
                  background:
                    "linear-gradient(135deg, #D4A03C 0%, #C6933A 100%)",
                }}
                asChild
              >
                <Link to="/guruvani">
                  <LotusIcon className="mr-2 h-4 w-4" />
                  Explore Guruvani
                </Link>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="rounded-lg px-6 py-5 text-sm font-medium transition-all"
                style={{
                  fontFamily: "Inter, sans-serif",
                  backgroundColor: "white",
                  borderColor: "#E5E0D5",
                  color: "#2D2A26",
                }}
                asChild
              >
                <Link to="/research">
                  <Search className="mr-2 h-4 w-4" />
                  Research Tools
                </Link>
              </Button>
            </div>

            {/* Quick Links */}
            <div className="flex flex-wrap gap-6 pt-3">
              <Link
                to="/library"
                className="flex items-center gap-2 text-sm transition-opacity hover:opacity-70"
                style={{ fontFamily: "Inter, sans-serif", color: "#6B6764" }}
              >
                <Library className="h-4 w-4" />
                <span className="font-medium">Library</span>
              </Link>
              <Link
                to="/books"
                className="flex items-center gap-2 text-sm transition-opacity hover:opacity-70"
                style={{ fontFamily: "Inter, sans-serif", color: "#6B6764" }}
              >
                <ScrollText className="h-4 w-4" />
                <span className="font-medium">Manuscripts</span>
              </Link>
              <Link
                to="/scholars"
                className="flex items-center gap-2 text-sm transition-opacity hover:opacity-70"
                style={{ fontFamily: "Inter, sans-serif", color: "#6B6764" }}
              >
                <Users className="h-4 w-4" />
                <span className="font-medium">Multi-Scholars</span>
              </Link>
            </div>
          </div>

          {/* Hero Visual */}
          <div className="relative order-1 flex justify-center lg:order-2">
            <div className="relative">
              {/* Main Image Card */}
              <div
                className="relative overflow-hidden rounded-2xl p-4 shadow-xl"
                style={{ backgroundColor: "white" }}
              >
                {/* Main Image */}
                <img
                  src={gurudevBio.mainImage}
                  alt="Gurudev Muni Jambuvijayaji Maharaj Saheb"
                  className="h-auto w-full max-w-xs rounded-xl object-contain lg:max-w-sm"
                />

                {/* Caption at bottom of image */}
                <div className="pb-1 pt-3 text-center">
                  <p
                    className="text-sm font-semibold"
                    style={{
                      fontFamily: "Inter, sans-serif",
                      color: "#2D2A26",
                    }}
                  >
                    Gurudev Muni Jambuvijayaji Maharaj Saheb
                  </p>
                  <p
                    className="text-xs"
                    style={{
                      fontFamily: "Inter, sans-serif",
                      color: "#9B9B9B",
                    }}
                  >
                    (1923–2009)
                  </p>
                </div>
              </div>

              {/* Floating Lotus Icon - Top Right */}
              <div className="animate-float absolute -right-3 -top-3">
                <div
                  className="flex h-14 w-14 items-center justify-center rounded-full shadow-lg lg:h-16 lg:w-16"
                  style={{
                    backgroundColor: "#FDF8EC",
                    border: "2px solid #F5EFE0",
                  }}
                >
                  <LotusIcon className="h-7 w-7 text-[#C9A227] lg:h-8 lg:w-8" />
                </div>
              </div>

              {/* Floating Book Icon - Bottom Left */}
              <div className="animate-float absolute -bottom-2 -left-3 delay-500">
                <div
                  className="flex h-12 w-12 items-center justify-center rounded-full shadow-lg"
                  style={{
                    backgroundColor: "#FDF8EC",
                    border: "2px solid #F5EFE0",
                  }}
                >
                  <BookOpen className="h-5 w-5 text-[#C9A227]" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
